import React, { useState, useEffect } from 'react';
import { brandService } from '../services/brandService';
import { domainService } from '../services/domainService';
import { bulkService } from '../services/bulkService';
import { useAuth } from '../contexts/AuthContext';

const Dashboard = () => {
  const { isAdmin } = useAuth();
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [domains, setDomains] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showAddBrand, setShowAddBrand] = useState(false);
  const [showEditBrand, setShowEditBrand] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [bulkTarget, setBulkTarget] = useState('');
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState({ total: 0, withRedirect: 0, withoutRedirect: 0 });

  useEffect(() => { loadBrands(); }, []);
  useEffect(() => { if (selectedBrand) loadDomains(selectedBrand.id, 1); }, [search]);

  const loadBrands = async () => {
    try {
      const res = await brandService.getAll();
      setBrands(res.data.brands);
      if (res.data.brands.length > 0 && !selectedBrand) {
        setSelectedBrand(res.data.brands[0]);
        loadDomains(res.data.brands[0].id, 1);
      }
    } catch (err) { console.error('Failed to load brands:', err); }
  };

  const loadDomains = async (brandId, page = 1) => {
    setLoading(true);
    try {
      const res = await domainService.getAll(brandId, { page, limit: 50, search });
      setDomains(res.data.domains);
      setPagination(res.data.pagination);
      const withRedirect = res.data.domains.filter(d => d.has_redirect).length;
      setStats({ total: res.data.pagination.total, withRedirect, withoutRedirect: res.data.pagination.total - withRedirect });
    } catch (err) { console.error('Failed to load domains:', err); }
    finally { setLoading(false); }
  };

  const syncDomains = async () => {
    if (!selectedBrand) return;
    setSyncing(true);
    try {
      const res = await domainService.sync(selectedBrand.id);
      alert(`✅ Synced! ${res.data.count} domains processed.`);
      await loadBrands(); await loadDomains(selectedBrand.id, 1);
    } catch (err) { alert(`❌ ${err.response?.data?.error || 'Failed'}`); }
    finally { setSyncing(false); }
  };

  const handleEditBrand = (brand) => { setEditingBrand(brand); setShowEditBrand(true); };
  
  const handleDeleteBrand = async (brand) => {
    if (!window.confirm(`Delete "${brand.name}"? This will delete all domains.`)) return;
    try {
      await brandService.delete(brand.id);
      alert('✅ Deleted!');
      await loadBrands();
      if (selectedBrand?.id === brand.id) setSelectedBrand(brands[0] || null);
    } catch (err) { alert('❌ Failed: ' + (err.response?.data?.error || err.message)); }
  };

  const handleSingleRedirect = async (domainId, targetUrl) => {
    try {
      await domainService.createRedirect(domainId, targetUrl);
      await loadDomains(selectedBrand.id, pagination.page);
      alert('✅ Redirect created!');
    } catch (err) { alert('❌ ' + (err.response?.data?.error || err.message)); }
  };

  const handleBulkRedirect = async () => {
    if (selectedDomains.length === 0 || !bulkTarget) { alert('⚠️ Select domains and enter URL'); return; }
    try {
      await bulkService.createBulkRedirect(selectedBrand.id, selectedDomains, bulkTarget);
      alert('✅ Bulk redirect started!');
      setSelectedDomains([]); setBulkTarget('');
      setTimeout(() => loadDomains(selectedBrand.id, pagination.page), 5000);
    } catch (err) { alert('❌ ' + (err.response?.data?.error || err.message)); }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      loadDomains(selectedBrand.id, newPage); setSelectedDomains([]);
    }
  };

  return (
    <div style={s.container}>
      <div style={{...s.sidebar, width: sidebarOpen ? '280px' : '0px', padding: sidebarOpen ? '20px' : '0'}}>
        {sidebarOpen && (<>
          <div style={s.sidebarHeader}><h3 style={s.sidebarTitle}>📁 Brands ({brands.length})</h3></div>
          {isAdmin && <button onClick={() => setShowAddBrand(true)} style={s.addBtn}>+ Add New Brand</button>}
          <div style={s.brandList}>
            {brands.map(b => (
              <div key={b.id} style={{marginBottom: '8px'}}>
                <div onClick={() => { setSelectedBrand(b); loadDomains(b.id, 1); setSelectedDomains([]); }}
                  style={{...s.brandItem, ...(selectedBrand?.id === b.id ? s.brandActive : {})}}>
                  <div style={s.brandName}>{b.name}</div>
                  <div style={s.brandCount}>{b.total_domains || 0} domains</div>
                </div>
                {isAdmin && selectedBrand?.id === b.id && (
                  <div style={s.brandActions}>
                    <button onClick={() => handleEditBrand(b)} style={s.editBtn2}>✏️ Edit</button>
                    <button onClick={() => handleDeleteBrand(b)} style={s.delBtn}>🗑️ Delete</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>)}
      </div>

      <button onClick={() => setSidebarOpen(!sidebarOpen)} style={s.toggle}>{sidebarOpen ? '◀' : '▶'}</button>

      <div style={s.main}>
        <div style={s.header}>
          <div style={{flex: 1}}>
            <h2 style={s.title}>Dashboard</h2>
            {selectedBrand && (
              <select value={selectedBrand.id} onChange={(e) => {
                const b = brands.find(x => x.id === e.target.value);
                setSelectedBrand(b); loadDomains(b.id, 1);
              }} style={s.select}>
                {brands.map(b => <option key={b.id} value={b.id}>{b.name} ({b.total_domains || 0})</option>)}
              </select>
            )}
          </div>
          {isAdmin && !sidebarOpen && <button onClick={() => setShowAddBrand(true)} style={s.addBtnTop}>+ Brand</button>}
        </div>

        {selectedBrand && (<>
          <div style={s.stats}>
            <div style={s.card}><div style={s.label}>Total Domains</div><div style={s.value}>{stats.total}</div></div>
            <div style={s.card}><div style={s.label}>With Redirect</div><div style={{...s.value, color: '#10b981'}}>{stats.withRedirect}</div></div>
            <div style={s.card}><div style={s.label}>Without Redirect</div><div style={{...s.value, color: '#f59e0b'}}>{stats.withoutRedirect}</div></div>
          </div>

          <div style={s.actions}>
            <input type="text" placeholder="🔍 Search..." value={search} onChange={(e) => setSearch(e.target.value)} style={s.search} />
            <button onClick={syncDomains} style={s.sync} disabled={syncing || loading}>{syncing ? '⏳ Syncing...' : '🔄 Sync'}</button>
          </div>

          {selectedDomains.length > 0 && (
            <div style={s.bulk}>
              <span style={s.bulkLabel}>✓ {selectedDomains.length} selected</span>
              <input type="url" placeholder="https://target.com" value={bulkTarget} onChange={(e) => setBulkTarget(e.target.value)} style={s.bulkInput} />
              <button onClick={handleBulkRedirect} style={s.bulkBtn}>Bulk Redirect →</button>
            </div>
          )}

          <div style={s.table}>
            {loading ? <div style={s.loading}>⏳ Loading...</div> : domains.length === 0 ? (
              <div style={s.empty}><div style={s.emptyIcon}>📦</div><p>No domains. Click Sync.</p></div>
            ) : (<>
              <table style={{width: '100%', borderCollapse: 'collapse'}}>
                <thead><tr>
                  <th style={{...s.th, width: '50px'}}><input type="checkbox" checked={selectedDomains.length === domains.length} onChange={(e) => setSelectedDomains(e.target.checked ? domains.map(d => d.id) : [])} /></th>
                  <th style={s.th}>Domain</th>
                  <th style={{...s.th, width: '100px'}}>Status</th>
                  <th style={s.th}>Current Redirect</th>
                  <th style={{...s.th, width: '120px'}}>Actions</th>
                </tr></thead>
                <tbody>{domains.map(d => <DomainRow key={d.id} domain={d} selected={selectedDomains.includes(d.id)} 
                  onSelect={(c) => setSelectedDomains(c ? [...selectedDomains, d.id] : selectedDomains.filter(i => i !== d.id))}
                  onRedirect={handleSingleRedirect} />)}
                </tbody>
              </table>
              <div style={s.pag}>
                <div style={s.pagInfo}>{((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}</div>
                <div style={s.pagBtns}>
                  <button onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page === 1} style={{...s.pagBtn, opacity: pagination.page === 1 ? 0.5 : 1}}>← Prev</button>
                  <span style={s.pagNum}>{pagination.page} / {pagination.totalPages}</span>
                  <button onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page >= pagination.totalPages} style={{...s.pagBtn, opacity: pagination.page >= pagination.totalPages ? 0.5 : 1}}>Next →</button>
                </div>
              </div>
            </>)}
          </div>
        </>)}
      </div>

      {showAddBrand && <BrandModal onClose={() => setShowAddBrand(false)} onSuccess={() => { loadBrands(); setShowAddBrand(false); }} />}
      {showEditBrand && editingBrand && <BrandModal brand={editingBrand} onClose={() => { setShowEditBrand(false); setEditingBrand(null); }} 
        onSuccess={() => { loadBrands(); setShowEditBrand(false); setEditingBrand(null); }} />}
    </div>
  );
};

const DomainRow = ({ domain, selected, onSelect, onRedirect }) => {
  const [url, setUrl] = useState(domain.redirect_url || '');
  const [editing, setEditing] = useState(false);
  return (
    <tr style={s.tr}>
      <td style={s.td}><input type="checkbox" checked={selected} onChange={(e) => onSelect(e.target.checked)} /></td>
      <td style={s.td}><strong>{domain.domain_name}</strong></td>
      <td style={s.td}><span style={domain.status === 'active' ? s.badge : s.badgeOff}>{domain.status}</span></td>
      <td style={s.td}>{editing ? <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} style={s.input2} placeholder="https://target.com" /> : <span style={s.redir}>{domain.redirect_url || 'Not set'}</span>}</td>
      <td style={s.td}>{editing ? (
        <div style={{display: 'flex', gap: '6px'}}>
          <button onClick={() => { if (url) { onRedirect(domain.id, url); setEditing(false); } else alert('Enter URL'); }} style={s.save}>✓</button>
          <button onClick={() => { setEditing(false); setUrl(domain.redirect_url || ''); }} style={s.cancel2}>✕</button>
        </div>
      ) : <button onClick={() => setEditing(true)} style={s.edit}>Edit</button>}</td>
    </tr>
  );
};

const BrandModal = ({ brand, onClose, onSuccess }) => {
  const [name, setName] = useState(brand?.name || '');
  const [token, setToken] = useState(brand?.cloudflare_api_token || '');
  const [desc, setDesc] = useState(brand?.description || '');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      if (brand) await brandService.update(brand.id, { name, cloudflare_api_token: token, description: desc });
      else await brandService.create({ name, cloudflare_api_token: token, description: desc });
      onSuccess();
    } catch (err) { alert('Failed: ' + (err.response?.data?.error || 'Error')); }
    finally { setLoading(false); }
  };

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={s.modalTitle}>{brand ? 'Edit Brand' : 'Add Brand'}</h3>
        <form onSubmit={handleSubmit}>
          <div style={s.group}><label style={s.lbl}>Brand Name *</label><input type="text" value={name} onChange={(e) => setName(e.target.value)} style={s.inp} required /></div>
          <div style={s.group}><label style={s.lbl}>API Token *</label><input type="text" value={token} onChange={(e) => setToken(e.target.value)} style={s.inp} required /><small style={s.hint}>Zone:Read + Page Rules:Edit</small></div>
          <div style={s.group}><label style={s.lbl}>Description</label><textarea value={desc} onChange={(e) => setDesc(e.target.value)} style={s.txtarea} /></div>
          <div style={s.acts}>
            <button type="button" onClick={onClose} style={s.cancelBtn}>Cancel</button>
            <button type="submit" style={s.submitBtn} disabled={loading}>{loading ? 'Saving...' : (brand ? 'Update' : 'Create')}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const s = {
  container: {display: 'flex', minHeight: 'calc(100vh - 60px)', background: '#f9fafb', position: 'relative'},
  sidebar: {background: 'white', borderRight: '1px solid #e5e7eb', transition: 'all 0.3s', overflow: 'hidden', flexShrink: 0},
  sidebarHeader: {marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #e5e7eb'},
  sidebarTitle: {margin: 0, fontSize: '18px', fontWeight: '600', color: '#111'},
  toggle: {position: 'fixed', left: '0', top: '80px', background: '#667eea', color: 'white', border: 'none', padding: '12px 8px', cursor: 'pointer', zIndex: 100, borderRadius: '0 6px 6px 0', fontSize: '16px', boxShadow: '2px 2px 8px rgba(0,0,0,0.1)'},
  brandList: {marginTop: '12px'},
  addBtn: {width: '100%', padding: '12px', background: '#667eea', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', marginBottom: '16px', fontSize: '14px', fontWeight: '600'},
  addBtnTop: {padding: '10px 20px', background: '#667eea', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500'},
  brandItem: {padding: '14px', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s', border: '1px solid transparent'},
  brandActive: {background: '#667eea', color: 'white', border: '1px solid #667eea'},
  brandName: {fontWeight: '600', fontSize: '14px', marginBottom: '4px'},
  brandCount: {fontSize: '12px', opacity: 0.8},
  brandActions: {display: 'flex', gap: '6px', marginTop: '6px', paddingLeft: '14px'},
  editBtn2: {flex: 1, padding: '6px 12px', background: '#10b981', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '500'},
  delBtn: {flex: 1, padding: '6px 12px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '500'},
  main: {flex: 1, padding: '24px', overflowX: 'hidden'},
  header: {display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', gap: '16px'},
  title: {fontSize: '28px', fontWeight: 'bold', margin: '0 0 12px 0', color: '#111'},
  select: {padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', background: 'white', minWidth: '250px', fontWeight: '500'},
  stats: {display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px'},
  card: {background: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #f3f4f6'},
  label: {fontSize: '13px', color: '#6b7280', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '500'},
  value: {fontSize: '36px', fontWeight: 'bold', color: '#111'},
  actions: {background: 'white', padding: '16px', borderRadius: '12px', marginBottom: '16px', display: 'flex', gap: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)'},
  search: {flex: 1, padding: '12px 16px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '14px'},
  sync: {padding: '12px 24px', background: 'white', color: '#667eea', border: '2px solid #667eea', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600'},
  bulk: {background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '20px', borderRadius: '12px', marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'center', boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'},
  bulkLabel: {fontSize: '15px', fontWeight: '700', color: 'white'},
  bulkInput: {flex: 1, padding: '12px 16px', border: 'none', borderRadius: '8px', fontSize: '14px'},
  bulkBtn: {padding: '12px 24px', background: 'white', color: '#667eea', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '700'},
  table: {background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)'},
  th: {textAlign: 'left', padding: '18px', background: '#f9fafb', borderBottom: '2px solid #e5e7eb', fontWeight: '600', fontSize: '13px', color: '#374151', textTransform: 'uppercase'},
  tr: {borderBottom: '1px solid #f3f4f6'},
  td: {padding: '16px 18px', fontSize: '14px'},
  badge: {display: 'inline-block', padding: '4px 12px', background: '#d1fae5', color: '#065f46', borderRadius: '16px', fontSize: '12px', fontWeight: '600'},
  badgeOff: {display: 'inline-block', padding: '4px 12px', background: '#fee2e2', color: '#991b1b', borderRadius: '16px', fontSize: '12px', fontWeight: '600'},
  redir: {color: '#6b7280', fontSize: '13px'},
  input2: {padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '6px', width: '100%', fontSize: '13px'},
  edit: {padding: '8px 16px', background: '#667eea', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500'},
  save: {padding: '8px 14px', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600'},
  cancel2: {padding: '8px 14px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600'},
  pag: {padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f3f4f6'},
  pagInfo: {fontSize: '14px', color: '#6b7280', fontWeight: '500'},
  pagBtns: {display: 'flex', gap: '12px', alignItems: 'center'},
  pagBtn: {padding: '10px 20px', background: '#667eea', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600'},
  pagNum: {fontSize: '14px', fontWeight: '700', color: '#374151', padding: '0 8px'},
  loading: {padding: '80px', textAlign: 'center', color: '#6b7280', fontSize: '16px'},
  empty: {padding: '80px 40px', textAlign: 'center'},
  emptyIcon: {fontSize: '64px', marginBottom: '16px'},
  overlay: {position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)'},
  modal: {background: 'white', padding: '36px', borderRadius: '16px', maxWidth: '540px', width: '90%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'},
  modalTitle: {margin: '0 0 28px 0', fontSize: '24px', fontWeight: '700', color: '#111'},
  group: {marginBottom: '24px'},
  lbl: {display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#374151'},
  inp: {padding: '12px 16px', border: '1px solid #e5e7eb', borderRadius: '8px', width: '100%', fontSize: '14px'},
  txtarea: {padding: '12px 16px', border: '1px solid #e5e7eb', borderRadius: '8px', width: '100%', minHeight: '100px', fontSize: '14px', fontFamily: 'inherit'},
  hint: {display: 'block', marginTop: '6px', fontSize: '12px', color: '#6b7280', fontStyle: 'italic'},
  acts: {display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '32px'},
  cancelBtn: {padding: '12px 24px', background: '#f3f4f6', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#374151'},
  submitBtn: {padding: '12px 32px', background: '#667eea', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600'},
};

export default Dashboard;
